import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Header,
  Segment,
  Message,
  Label,
  Icon,
  Form,
  Confirm,
  List,
  Modal
} from 'semantic-ui-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../../firebase/config';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { formatDate } from '../../../utils/dateUtils';
import { useProctoringLogs } from '../../../hooks/useProctoringLogs';
import { useExamLockdown } from '../../../hooks/useExamLockdown';
import ProctorCamera from './ProctorCamera';
import ExamDisclaimer from './ExamDisclaimer';

interface CloseBookQuestion {
  id: string;
  text: string;
  points: number;
}

interface Test {
  id: string;
  testName: string;
  type: 'closeBook' | 'verseMemorization';
  totalGrade: number;
  examStatus?: 'notStarted' | 'inProgress' | 'ended';
  criteria?: {
    shortAnswer: { description: string; grade: number };
    longAnswer: { description: string; grade: number };
  };
  questions?: {
    shortAnswer: CloseBookQuestion[];
    longAnswer: CloseBookQuestion[];
  };
  config?: {
    shortAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
    longAnswer: { totalQuestions: number; requiredAnswers: number; pointsPerQuestion: number };
  };
}

interface ViewCloseBookTestProps {
  test: Test;
  moduleId: string;
  onBack: () => void;
  onExamLockChange?: (locked: boolean) => void;
}

const ViewCloseBookTest: React.FC<ViewCloseBookTestProps> = ({
  test,
  moduleId,
  onBack,
  onExamLockChange
}) => {
  const [user] = useAuthState(auth);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submission, setSubmission] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showSecondConfirm, setShowSecondConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningNumber, setWarningNumber] = useState(0);
  const hasAutoSubmittedRef = useRef(false);
  const hasExistingSubmissionRef = useRef(false);
  const lastViolationTimeRef = useRef(0);

  // Close book: answers keyed by questionId
  const [shortAnswers, setShortAnswers] = useState<Record<string, string>>({});
  const [longAnswers, setLongAnswers] = useState<Record<string, string>>({});
  // Verse memorization
  const [verseAnswer, setVerseAnswer] = useState('');

  const examInProgress = test.examStatus === 'inProgress';
  const hasSubmitted =
    (submission?.answers?.shortAnswer ||
    submission?.answers?.longAnswer ||
    submission?.answer !== undefined) &&
    !submission?.resumeAllowed;

  const antiCheatEnabled =
    userRole === 'student' &&
    examInProgress &&
    disclaimerAccepted &&
    !hasSubmitted;

  const handleSubmitRef = useRef<(isAutoSubmit?: boolean, reason?: 'violation' | 'examEnd') => Promise<void>>();

  const handleSubmit = useCallback(async (isAutoSubmit?: boolean, reason?: 'violation' | 'examEnd') => {
    if (!user) return;
    if (isAutoSubmit && (hasAutoSubmittedRef.current || hasExistingSubmissionRef.current)) return; // Prevent double auto-submit or overwriting existing
    if (isAutoSubmit) hasAutoSubmittedRef.current = true; // Block re-entry from rapid violation triggers
    setShowSecondConfirm(false);

    if (!isAutoSubmit) {
      if (test.type !== 'closeBook' && !verseAnswer.trim()) {
        setError('Please enter your answer.');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const submissionRef = doc(
        db,
        'modules',
        moduleId,
        'tests',
        test.id,
        'submissions',
        user.uid
      );

      if (test.type === 'closeBook' && test.questions) {
        const shortAnswerArr = test.questions.shortAnswer
          .filter(q => shortAnswers[q.id]?.trim())
          .map(q => ({ questionId: q.id, answer: shortAnswers[q.id].trim() }));
        const longAnswerArr = test.questions.longAnswer
          .filter(q => longAnswers[q.id]?.trim())
          .map(q => ({ questionId: q.id, answer: longAnswers[q.id].trim() }));

        await setDoc(submissionRef, {
          studentId: user.uid,
          submittedAt: new Date(),
          type: 'closeBook',
          gradingStatus: 'pending',
          answers: {
            shortAnswer: shortAnswerArr,
            longAnswer: longAnswerArr
          }
        });
      } else {
        await setDoc(submissionRef, {
          studentId: user.uid,
          submittedAt: new Date(),
          type: 'verseMemorization',
          gradingStatus: 'pending',
          answer: verseAnswer.trim()
        });
      }

      setSuccess(
        reason === 'violation'
          ? 'Your exam has been automatically submitted due to repeated anti-cheat violations. You cannot make changes.'
          : reason === 'examEnd'
          ? 'The exam has ended. Your answers have been automatically submitted. You cannot make changes.'
          : 'Your exam has been submitted successfully. You cannot make changes.'
      );
      hasExistingSubmissionRef.current = true;
      const subDoc = await getDoc(submissionRef);
      setSubmission(subDoc.data());
    } catch (err) {
      console.error('Error submitting:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [user, test, moduleId, shortAnswers, longAnswers, verseAnswer]);

  handleSubmitRef.current = handleSubmit;

  const VIOLATION_DEBOUNCE_MS = 500;

  const onViolation = useCallback(() => {
    const now = Date.now();
    if (now - lastViolationTimeRef.current < VIOLATION_DEBOUNCE_MS) return;
    lastViolationTimeRef.current = now;

    setViolationCount(prev => {
      const next = prev + 1;
      if (next >= 3 && !hasAutoSubmittedRef.current) {
        handleSubmitRef.current?.(true, 'violation');
      } else if (next <= 2) {
        setWarningNumber(next);
        setShowWarningModal(true);
      }
      return next;
    });
  }, []);

  useProctoringLogs(test.id, moduleId, user?.uid, antiCheatEnabled, onViolation);
  useExamLockdown(test.id, moduleId, antiCheatEnabled, onViolation);

  useEffect(() => {
    const locked = examInProgress && disclaimerAccepted && !hasSubmitted;
    onExamLockChange?.(locked);
    return () => onExamLockChange?.(false);
  }, [examInProgress, disclaimerAccepted, hasSubmitted, onExamLockChange]);

  // Auto-submit when exam ends while student has page open (status transitions to 'ended')
  const prevExamStatusRef = useRef<string | undefined>(test.examStatus);
  useEffect(() => {
    if (!user || userRole !== 'student') return;

    const testRef = doc(db, 'modules', moduleId, 'tests', test.id);
    const unsubscribe = onSnapshot(testRef, (snap) => {
      const data = snap.data();
      const newStatus = data?.examStatus;
      const prevStatus = prevExamStatusRef.current;
      prevExamStatusRef.current = newStatus;

      // Only auto-submit when status transitions from inProgress to ended (admin ended exam while student had page open)
      if (prevStatus === 'inProgress' && newStatus === 'ended') {
        handleSubmitRef.current?.(true, 'examEnd');
      }
    });

    return () => unsubscribe();
  }, [user, moduleId, test.id, userRole]);

  useEffect(() => {
    if (!user) return;

    const fetchUserRole = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        setUserRole(userData?.role || null);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };
    fetchUserRole();

    const submissionRef = doc(db, 'modules', moduleId, 'tests', test.id, 'submissions', user.uid);
    const unsubscribeSubmission = onSnapshot(submissionRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const hasAnswers = !!(data?.answers?.shortAnswer || data?.answers?.longAnswer || data?.answer !== undefined);

        if (data?.resumeAllowed) {
          hasExistingSubmissionRef.current = false;
          hasAutoSubmittedRef.current = false;
          setViolationCount(0);
          setWarningNumber(0);
          setShowWarningModal(false);
          setSuccess(null);
        } else {
          hasExistingSubmissionRef.current = hasAnswers;
        }

        setSubmission(data);

        if (data.answers?.shortAnswer) {
          const sa: Record<string, string> = {};
          data.answers.shortAnswer.forEach(
            (a: { questionId: string; answer: string }) => {
              sa[a.questionId] = a.answer;
            }
          );
          setShortAnswers(sa);
        }
        if (data.answers?.longAnswer) {
          const la: Record<string, string> = {};
          data.answers.longAnswer.forEach(
            (a: { questionId: string; answer: string }) => {
              la[a.questionId] = a.answer;
            }
          );
          setLongAnswers(la);
        }
        if (data.answer !== undefined) {
          setVerseAnswer(data.answer);
        }
      } else {
        hasExistingSubmissionRef.current = false;
        setSubmission(null);
      }
    }, (err) => {
      console.error('Error listening to submission:', err);
      setError('Failed to load data');
    });

    return () => unsubscribeSubmission();
  }, [user, moduleId, test.id]);

  const handleShortAnswerChange = (questionId: string, value: string) => {
    setShortAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleLongAnswerChange = (questionId: string, value: string) => {
    setLongAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const getUnansweredQuestions = () => {
    if (!test.questions) return { short: [], long: [] };
    const short = test.questions.shortAnswer
      .map((q, idx) => ({ idx: idx + 1, text: q.text }))
      .filter((_, i) => !shortAnswers[test.questions!.shortAnswer[i].id]?.trim());
    const long = test.questions.longAnswer
      .map((q, idx) => ({ idx: idx + 1, text: q.text }))
      .filter((_, i) => !longAnswers[test.questions!.longAnswer[i].id]?.trim());
    return { short, long };
  };

  const renderTestDetails = () => (
    <Segment>
      <Header as="h3">Test Details</Header>
      {test.type === 'closeBook' ? (
        test.config ? (
          <>
            <p>
              <strong>Short Answer:</strong> Answer {test.config.shortAnswer.requiredAnswers} of{' '}
              {test.config.shortAnswer.totalQuestions} questions @{' '}
              {test.config.shortAnswer.pointsPerQuestion} pts each
            </p>
            <p>
              <strong>Long Answer:</strong> Answer {test.config.longAnswer.requiredAnswers} of{' '}
              {test.config.longAnswer.totalQuestions} questions @{' '}
              {test.config.longAnswer.pointsPerQuestion} pts each
            </p>
            <p>
              <strong>Total Grade:</strong> {test.totalGrade} points
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>Short Answer Grade:</strong>{' '}
              {test.criteria?.shortAnswer?.grade ?? '-'}
            </p>
            <p>
              <strong>Long Answer Grade:</strong>{' '}
              {test.criteria?.longAnswer?.grade ?? '-'}
            </p>
          </>
        )
      ) : (
        <p>
          <strong>Total Grade:</strong> {test.totalGrade}
        </p>
      )}
      <Label
        color={
          examInProgress ? 'green' : test.examStatus === 'ended' ? 'grey' : 'red'
        }
      >
        Exam {examInProgress ? 'In Progress' : test.examStatus === 'ended' ? 'Ended' : 'Not Started'}
      </Label>
    </Segment>
  );

  const renderGradingInfo = () => {
    if (!submission?.gradingStatus || submission.gradingStatus === 'pending')
      return null;

    return (
      <Segment>
        <Header as="h3">Grading Results</Header>
        {test.type === 'closeBook' && submission.grades ? (
          <>
            <Header as="h4">Short Answer Questions</Header>
            <List>
              {submission.grades.shortAnswer?.map(
                (g: { questionId: string; grade: number; maxGrade: number; comment?: string }, idx: number) => {
                  const q = test.questions?.shortAnswer?.find(
                    x => x.id === g.questionId
                  );
                  return (
                    <List.Item key={g.questionId}>
                      <List.Content>
                        <List.Header>
                          Q{idx + 1}: {q?.text?.slice(0, 50)}...
                        </List.Header>
                        <List.Description>
                          Grade: {g.grade}/{g.maxGrade}
                          {g.comment && ` - ${g.comment}`}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                  );
                }
              )}
            </List>
            <Header as="h4">Long Answer Questions</Header>
            <List>
              {submission.grades.longAnswer?.map(
                (g: { questionId: string; grade: number; maxGrade: number; comment?: string }, idx: number) => {
                  const q = test.questions?.longAnswer?.find(
                    x => x.id === g.questionId
                  );
                  return (
                    <List.Item key={g.questionId}>
                      <List.Content>
                        <List.Header>
                          Q{idx + 1}: {q?.text?.slice(0, 50)}...
                        </List.Header>
                        <List.Description>
                          Grade: {g.grade}/{g.maxGrade}
                          {g.comment && ` - ${g.comment}`}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                  );
                }
              )}
            </List>
            <p>
              <strong>Total Grade:</strong> {submission.grade ?? submission.totalGrade ?? 0}/{test.totalGrade}
            </p>
          </>
        ) : (
          <p>
            <strong>Total Grade:</strong> {submission.grade ?? 0}/{test.totalGrade}
          </p>
        )}
        {submission.graderComments && (
          <>
            <Header as="h4">Grader Comments</Header>
            <p>{submission.graderComments}</p>
          </>
        )}
      </Segment>
    );
  };

  if (userRole !== 'student') {
    return (
      <div>
        <Button color="grey" icon labelPosition="left" onClick={onBack}>
          <Icon name="arrow left" />
          Back to Tests
        </Button>
        <Header as="h2">{test.testName}</Header>
        {renderTestDetails()}
      </div>
    );
  }

  const submittedAt = submission?.submittedAt;
  const submittedAtDate =
    submittedAt?.toDate ? submittedAt.toDate() : submittedAt;

  const isExamLocked = examInProgress && disclaimerAccepted && !hasSubmitted;

  return (
    <div>
      <Button
        color="grey"
        icon
        labelPosition="left"
        onClick={onBack}
        disabled={isExamLocked}
      >
        <Icon name="arrow left" />
        Back to Tests
      </Button>

      <Header as="h2">{test.testName}</Header>

      {error && <Message negative>{error}</Message>}
      {success && <Message positive>{success}</Message>}

      {renderTestDetails()}

      {!examInProgress && !hasSubmitted && (
        <Message warning>
          The exam is not currently in progress. Please wait for the teacher to
          start the exam.
        </Message>
      )}

      {examInProgress && !hasSubmitted && (
        <ProctorCamera studentId={user?.uid ?? ''} examId={test.id} moduleId={moduleId} enabled={true} />
      )}

      {examInProgress && !hasSubmitted && !disclaimerAccepted && (
        <ExamDisclaimer
          open={true}
          onAccept={() => setDisclaimerAccepted(true)}
          onCancel={onBack}
        />
      )}

      <Modal
        open={showWarningModal}
        size="small"
        closeOnDimmerClick={false}
        closeOnEscape={false}
      >
        <Modal.Header>
          <Icon name="warning sign" color="yellow" /> Anti-Cheat Warning {warningNumber} of 2
        </Modal.Header>
        <Modal.Content>
          {warningNumber === 1 ? (
            <p>
              You have violated the exam rules (e.g., switching tabs or copy/paste).
              This is your <strong>first warning</strong>. You have one more warning before your exam
              is automatically submitted.
            </p>
          ) : (
            <p>
              You have violated the exam rules again. This is your <strong>final warning</strong>.
              If you violate one more time, your exam will be <strong>automatically submitted</strong> and
              you will not be able to make any further changes.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={() => setShowWarningModal(false)}>
            I Understand
          </Button>
        </Modal.Actions>
      </Modal>

      {examInProgress && disclaimerAccepted && !hasSubmitted && (
        <>
          {test.type === 'closeBook' && test.questions ? (
            <Segment>
              <Header as="h3">Answer the Questions</Header>
              <p>
                Answer at least {test.config?.shortAnswer.requiredAnswers} short
                answer and {test.config?.longAnswer.requiredAnswers} long answer
                questions.
              </p>

              <Header as="h4">Short Answer Questions</Header>
              {test.questions.shortAnswer.map((q, idx) => (
                <Form.Field key={q.id}>
                  <label>
                    Question {idx + 1} ({q.points} pts)
                  </label>
                  <p>{q.text}</p>
                  <Form.TextArea
                    value={shortAnswers[q.id] ?? ''}
                    onChange={(e) =>
                      handleShortAnswerChange(q.id, e.target.value)
                    }
                    placeholder="Your answer..."
                    rows={6}
                    style={{ minHeight: '120px', width: '100%' }}
                  />
                </Form.Field>
              ))}

              <Header as="h4">Long Answer Questions</Header>
              {test.questions.longAnswer.map((q, idx) => (
                <Form.Field key={q.id}>
                  <label>
                    Question {idx + 1} ({q.points} pts)
                  </label>
                  <p>{q.text}</p>
                  <Form.TextArea
                    value={longAnswers[q.id] ?? ''}
                    onChange={(e) =>
                      handleLongAnswerChange(q.id, e.target.value)
                    }
                    placeholder="Your answer..."
                    rows={10}
                    style={{ minHeight: '200px', width: '100%' }}
                  />
                </Form.Field>
              ))}

              <Button
                primary
                onClick={() => setShowSubmitConfirm(true)}
                disabled={isSubmitting}
              >
                Submit Exam
              </Button>
            </Segment>
          ) : test.type === 'verseMemorization' ? (
            <Segment>
              <Header as="h3">Your Answer</Header>
              <Form.TextArea
                value={verseAnswer}
                onChange={(e) => setVerseAnswer(e.target.value)}
                placeholder="Enter your verse memorization..."
                rows={12}
                style={{ minHeight: '250px', width: '100%' }}
              />
              <Button
                primary
                onClick={() => setShowSubmitConfirm(true)}
                disabled={isSubmitting}
              >
                Submit Exam
              </Button>
            </Segment>
          ) : (
            <Message warning>
              This test format is not supported for online exams. Please contact
              your teacher.
            </Message>
          )}
        </>
      )}

      {hasSubmitted && (
        <Segment>
          <Message info>
            <Message.Header>Test Submitted</Message.Header>
            <p>You have already submitted your exam.</p>
            <p>Submitted on: {formatDate(submittedAtDate)}</p>
          </Message>

          {test.type === 'closeBook' && test.questions && submission?.answers && (
            <>
              <Header as="h4">Your Answers</Header>
              {submission.answers.shortAnswer?.map(
                (a: { questionId: string; answer: string }) => {
                  const q = test.questions!.shortAnswer.find(
                    x => x.id === a.questionId
                  );
                  return (
                    <Segment key={a.questionId}>
                      <p>
                        <strong>{q?.text}</strong>
                      </p>
                      <p>{a.answer}</p>
                    </Segment>
                  );
                }
              )}
              {submission.answers.longAnswer?.map(
                (a: { questionId: string; answer: string }) => {
                  const q = test.questions!.longAnswer.find(
                    x => x.id === a.questionId
                  );
                  return (
                    <Segment key={a.questionId}>
                      <p>
                        <strong>{q?.text}</strong>
                      </p>
                      <p>{a.answer}</p>
                    </Segment>
                  );
                }
              )}
            </>
          )}
          {test.type === 'verseMemorization' && submission?.answer && (
            <Segment>
              <Header as="h4">Your Answer</Header>
              <p style={{ whiteSpace: 'pre-wrap' }}>{submission.answer}</p>
            </Segment>
          )}
        </Segment>
      )}

      {submission && renderGradingInfo()}

      <Modal
        open={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        size="small"
      >
        <Modal.Header>Submit Exam</Modal.Header>
        <Modal.Content>
          {(() => {
            const unanswered = getUnansweredQuestions();
            const hasUnanswered = unanswered.short.length > 0 || unanswered.long.length > 0;

            if (!hasUnanswered) {
              return (
                <p>Are you sure you want to submit your exam? You will NOT be able to make any changes after submission.</p>
              );
            }

            return (
              <>
                <Message warning>
                  <Message.Header>You have unanswered questions</Message.Header>
                  <p>The following questions have not been answered. Unanswered questions will not be graded.</p>
                </Message>
                {unanswered.short.length > 0 && (
                  <>
                    <Header as="h5">Short Answer</Header>
                    <List bulleted>
                      {unanswered.short.map(q => (
                        <List.Item key={q.idx}>
                          Question {q.idx}: {q.text.length > 80 ? q.text.slice(0, 80) + '...' : q.text}
                        </List.Item>
                      ))}
                    </List>
                  </>
                )}
                {unanswered.long.length > 0 && (
                  <>
                    <Header as="h5">Long Answer</Header>
                    <List bulleted>
                      {unanswered.long.map(q => (
                        <List.Item key={q.idx}>
                          Question {q.idx}: {q.text.length > 80 ? q.text.slice(0, 80) + '...' : q.text}
                        </List.Item>
                      ))}
                    </List>
                  </>
                )}
                <p style={{ marginTop: '1em' }}>
                  You can still submit with unanswered questions. Are you sure you want to continue?
                </p>
              </>
            );
          })()}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowSubmitConfirm(false)}>
            Go Back
          </Button>
          <Button
            primary
            onClick={() => {
              setShowSubmitConfirm(false);
              setShowSecondConfirm(true);
            }}
          >
            Continue to Submit
          </Button>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={showSecondConfirm}
        onCancel={() => setShowSecondConfirm(false)}
        onConfirm={() => handleSubmit()}
        content="This is your final confirmation. Once you submit, you cannot go back to the exam. Are you absolutely sure?"
        confirmButton="Submit Final"
      />
    </div>
  );
};

export default ViewCloseBookTest;
